import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import debounce from 'lodash.debounce';
import { Modal, Grid, TextField, Checkbox, FormControlLabel, Typography, ListItem, ListItemText } from '@mui/material';
import { FormContainer, SectionTitle, RowContainer, BaseLabel, Label, AutoComplete, cores } from '../../../styles/styleUtils'; // Reutilizando seus estilos
import OsActionButtons from '../components/ActionButtonsOs';
import OsAnexos from './OsAnexos'; // Componente para lidar com anexos
import ModalLoading from '../../ModalLoading';
import { useUnidades } from '../../../hooks/useUnidades';
import CampoMonetario from '../components/CampoMonetario';

// API (assumindo que você tem um arquivo de API)
import {
  getClientesPagina,
  getFornecedores,
  getGenericData,
  getInversores,
  createOs,
  getAnexosByOsId,
  getFluxogramas,
} from '../../api'; // 'createOs' é uma nova função da API

// Helper para data
const hojeNoFusoBrasileiro = () => {
  const now = new Date();
  const localDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return localDate;
};

const Style1 = styled(TextField)`
  width: 70%;
  border-radius: 10px;
  box-sizing: border-box;
  background-color: ${cores.branco};
  & .MuiInputBase-root {
    height: 36px;
    padding: 1.5px;
  }
`;

// Componente
const NewOsForm = React.forwardRef((props, ref) => {
  const { unidades } = useUnidades();
  const getInitialState = () => ({
    data: hojeNoFusoBrasileiro(),
    idCliente: null,
    cliente: null,
    cidade: '',
    unidade: null, // Ajuste para objeto se necessário
    telefone: '',
    descricao: '',
    idClassificacao: null,
    idFornecedor: null,
    fornecedor: null,
    idDistribuidora: null,
    distribuidora: null,
    idInversor: null,
    inversor: null,
    temCusto: false,
    valorCusto: 0,
    valorServico: 0,
    anexos: [], // Array de objetos { nome, base64, ext }
    status: 'Pendente',
    // user: ... (pegar do contexto de autenticação)
  });

  const [formData, setFormData] = useState(props.formData || getInitialState());
  const [modalLoading, setModalLoading] = useState(false);
  const [disableConfirm, setDisableConfirm] = useState(true);

  // --- Estados para Autocompletes ---
  const [fornecedorOptions, setFornecedorOptions] = useState([]);
  const [distribuidoraOptions, setDistribuidoraOptions] = useState([]);
  const [inversorOptions, setInversorOptions] = useState([]);
  const [classificacaoOptions, setClassificacaoOptions] = useState([]);
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');

  // --- Efeito para Carregar Dados Iniciais (Novo ou Edição) ---
  useEffect(() => {
    // Roda quando o modal abre (props.open) ou quando os dados mudam (props.formData)
    const loadData = async () => {
      if (props.open) {
        if (props.formData && props.formData._id) {
          // --- MODO DE EDIÇÃO ---
          // props.formData contém uma OS existente (do duplo clique)
          setModalLoading(true);
          try {
            // Busca os anexos existentes dessa OS
            const anexosExistentes = await getAnexosByOsId(props.formData._id);

            // Popula o formulário com os dados da OS + Anexos buscados
            setFormData({
              ...props.formData,
              anexos: anexosExistentes || [],
            });
          } catch (error) {
            console.error('Erro ao buscar anexos:', error);
            alert('Não foi possível carregar os anexos existentes. A OS será carregada sem eles.');
            // Carrega os dados da OS mesmo se os anexos falharem
            setFormData({ ...props.formData, anexos: [] });
          } finally {
            setModalLoading(false);
          }
        } else {
          // --- MODO DE CRIAÇÃO ---
          // Limpa o formulário para um estado inicial
          setFormData(getInitialState());
        }
      }
    };

    loadData();
  }, [props.formData, props.open]); // Dependências

  // --- Handlers de Input ---
  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDateChange = (newDate) => {
    setFormData((prev) => ({
      ...prev,
      data: newDate,
    }));
  };

  const handleAutoCompleteChange = (idName, name, value) => {
    setModalLoading(true);
    setFormData({
      ...formData,
      [idName]: value?._id,
      [name]: value,
    });
    setModalLoading(false);
  };

  useEffect(() => {
    if (formData.cliente) {
      setFormData({
        ...formData,
        estado: formData.cliente.estado?.nome,
        sigla: formData.cliente.estado?.sigla,
      });
    }
  }, [formData.cliente]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (value) => {
    const fetchedClientes = await getClientesPagina({ nome: value, page: 1, limit: 100 });
    const fetchedFornecedores = await getFornecedores();
    const fetchedDistribuidora = await getGenericData('distribuidora');
    const fetchedClassificacao = await getGenericData('classificacao');
    const fetchedInversor = await getInversores();

    setOptions(fetchedClientes.clientes);
    setFornecedorOptions(fetchedFornecedores);
    setDistribuidoraOptions(fetchedDistribuidora);
    setClassificacaoOptions(fetchedClassificacao);
    setInversorOptions(fetchedInversor);
  };

  // Sempre que digitar algo novo
  const handleInputChangeClient = (event, value, reason) => {
    setInputValue(value);
    if (reason === 'input') {
      debouncedFetch(value);
    }
  };

  // Debounce evita chamadas excessivas
  const debouncedFetch = debounce((value) => {
    if (value.length >= 2) {
      // mínimo de caracteres
      fetchData(value);
    }
  }, 500);

  // --- Lógica de Anexos ---

  const handleAddAnexo = (anexo) => {
    setFormData((prev) => ({
      ...prev,
      anexos: [...prev.anexos, anexo],
    }));
  };

  const handleRemoveAnexo = (index) => {
    setFormData((prev) => ({
      ...prev,
      anexos: prev.anexos.filter((_, i) => i !== index),
    }));
  };

  // --- Lógica de Submissão ---

  const handleSubmit = async (status) => {
    setModalLoading(true);

    formData.status = status;

    if (status === 'Aberta') {
      const fluxos = await getFluxogramas();

      if (formData.classificacao.nome === 'Garantia Inversor') {
        formData.fluxo = { id: 51, nome: 'OS Garantia' };
        formData.etapa = fluxos.find((fluxo) => fluxo.id === 51).etapas[0];
      } else {
        formData.fluxo = { id: 50, nome: 'OS' };
        formData.etapa = fluxos.find((fluxo) => fluxo.id === 50).etapas[0];
      }
    }

    try {
      props.onConfirm(formData); // Passa a OS criada de volta
      handleClose(); // Fecha e reseta
    } catch (error) {
      console.error('Erro ao submeter OS:', error);
      alert('Erro ao salvar a OS. Verifique o console.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleSave = () => handleSubmit('Pendente');
  const handleGenerate = () => handleSubmit('Aberta');

  const handleClose = () => {
    setFormData(getInitialState()); // Reseta o formulário
    props.handleClose();
  };

  const handleCancel = () => {
    if (window.confirm('Tem certeza que deseja sair? As alterações não salvas serão perdidas.')) {
      setFormData(getInitialState()); // Reseta o formulário
      props.handleClose();
    }
  };

  // --- Validação ---
  useEffect(() => {
    const { idCliente, descricao, unidade, classificacao, fornecedor, distribuidora, inversor } = formData;
    // Validação básica: precisa de cliente e descrição
    if (idCliente && descricao && unidade && classificacao && fornecedor && distribuidora && inversor) {
      setDisableConfirm(false);
    } else {
      setDisableConfirm(true);
    }
  }, [formData]);

  return (
    <Modal open={props.open} onClose={props.handleClose}>
      <FormContainer>
        <RowContainer>
          <Label htmlFor="cliente">Cliente</Label>
          <AutoComplete
            disablePortal
            id="cliente"
            name="cliente"
            value={formData.cliente || null}
            onChange={(_, value) => handleAutoCompleteChange('idCliente', 'cliente', value)}
            inputValue={inputValue}
            onInputChange={handleInputChangeClient}
            options={options}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            renderOption={(props, option) => (
              <ListItem key={option._id} {...props}>
                <ListItemText primary={option.nome} secondary={option.cpf} />
              </ListItem>
            )}
            getOptionLabel={(option) => `${option.nome} - ${option.cpf} (${option._id})`}
            renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
            disabled={formData.visualiza}
          />
        </RowContainer>
        {/* --- Seção Detalhes da OS --- */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <BaseLabel>Data</BaseLabel>
            {/* Usando TextField com type="date" para simplicidade.
              Se você usa @mui/x-date-pickers, substitua por <DatePicker />
            */}
            <Style1
              type="date"
              name="data"
              value={new Date(formData.data).toISOString().split('T')[0]} // Formato YYYY-MM-DD
              onChange={(e) => handleDateChange(new Date(e.target.value))}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ backgroundColor: cores.branco, borderRadius: 5 }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <BaseLabel htmlFor="unidade">Unidade</BaseLabel>
            <AutoComplete
              disablePortal
              id="unidade"
              value={formData.unidade || null}
              options={unidades}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              getOptionLabel={(option) => `${option.nome}`}
              renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
              onChange={(_, value) => handleAutoCompleteChange(null, 'unidade', value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <BaseLabel>Cidade</BaseLabel>
            <Style1
              name="cidade"
              value={formData.cliente?.cidade?.nome}
              onChange={handleInputChange}
              fullWidth
              placeholder="Ex: Uberlândia"
              sx={{ backgroundColor: cores.branco, borderRadius: 5 }}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <BaseLabel>Telefone</BaseLabel>
            <Style1
              name="telefone"
              value={formData.cliente?.telefone}
              onChange={handleInputChange}
              fullWidth
              placeholder="(XX) XXXX-XXXX"
              // TODO: Adicionar máscara de telefone (ex: react-input-mask)
              sx={{ backgroundColor: cores.branco, borderRadius: 5 }}
              disabled
            />
          </Grid>

          {/* --- Descrição --- */}
          <Grid item xs={12}>
            <Label>Descrição</Label>
            <TextField
              name="descricao"
              value={formData.descricao}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={4}
              placeholder="Detalhe o problema ou solicitação..."
              sx={{ backgroundColor: cores.branco, borderRadius: 5 }}
            />
          </Grid>

          {/* --- Classificação (Lista Estática) --- */}
          <Grid item xs={12} md={6}>
            <BaseLabel>Classificação</BaseLabel>
            <AutoComplete
              id="classificacao"
              options={classificacaoOptions}
              value={formData.classificacao || null}
              getOptionLabel={(option) => option.nome}
              onChange={(_, value) => handleAutoCompleteChange('idClassificacao', 'classificacao', value)}
              renderInput={(params) => <TextField {...params} placeholder="Selecione a classificação" />}
            />
          </Grid>

          {/* --- Fornecedor (Busca API) --- */}
          <Grid item xs={12} md={6}>
            <BaseLabel>Fornecedor</BaseLabel>
            <AutoComplete
              id="fornecedor"
              value={formData.fornecedor || null}
              onChange={(_, value) => handleAutoCompleteChange('idFornecedor', 'fornecedor', value)}
              options={fornecedorOptions}
              getOptionLabel={(option) => option.nome}
              renderInput={(params) => <TextField {...params} placeholder="Busque o fornecedor" />}
            />
          </Grid>

          {/* --- Distribuidora (Busca API) --- */}
          <Grid item xs={12} md={6}>
            <BaseLabel>Distribuidora</BaseLabel>
            <AutoComplete
              id="distribuidora"
              value={formData.distribuidora || null}
              onChange={(_, value) => handleAutoCompleteChange('idDistribuidora', 'distribuidora', value)}
              options={distribuidoraOptions}
              getOptionLabel={(option) => option.nome}
              renderInput={(params) => <TextField {...params} placeholder="Busque a distribuidora" />}
            />
          </Grid>

          {/* --- Modelo Inversor (Busca API 'Produtos') --- */}
          <Grid item xs={12} md={6}>
            <BaseLabel>Modelo Inversor</BaseLabel>
            <AutoComplete
              id="inversor"
              value={formData.inversor || null}
              onChange={(_, value) => handleAutoCompleteChange('idInversor', 'inversor', value)}
              options={inversorOptions}
              getOptionLabel={(option) => option.modelo} // Assumindo 'nome' do produto
              renderInput={(params) => <TextField {...params} placeholder="Busque o inversor" />}
            />
          </Grid>

          {/* --- Custo --- */}
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  name="temCusto"
                  checked={formData.temCusto}
                  onChange={handleInputChange}
                  sx={{ color: cores.textoClaro }}
                />
              }
              label="Haverá Custo para o cliente?"
              sx={{ color: cores.textoClaro }}
            />
            {formData.temCusto && (
              <>
                <BaseLabel>Valor</BaseLabel>
                <CampoMonetario
                  name={'valorCusto'}
                  valor={formData.valorCusto}
                  onChange={handleInputChange}
                  disabled={formData.visualiza}
                />
              </>
            )}
          </Grid>
        </Grid>
        {/* --- Seção Anexos --- */}
        <SectionTitle>Anexos</SectionTitle>
        <OsAnexos anexos={formData.anexos} onAddAnexo={handleAddAnexo} onRemoveAnexo={handleRemoveAnexo} />
        {/* --- Botões de Ação --- */}
        <OsActionButtons
          onCancel={handleCancel}
          onConfirm={handleSave}
          onGenerate={handleGenerate}
          disableConfirm={disableConfirm}
          visualiza={props.visualiza}
        />
        {/* --- Modal de Loading --- */}
        {modalLoading && <ModalLoading open={modalLoading} />}
      </FormContainer>
    </Modal>
  );
});

export default NewOsForm;